import { createApi } from '@reduxjs/toolkit/query/react';

import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';

import mentoApi from '@/api/mentoApi';
import { safeSetLocationHref } from '@/utils/authRedirect';

export interface AxiosBaseQueryError {
  data?: unknown;
  status?: number | string;
}

export interface AxiosBaseQueryMeta {
  status: number;
}

// Create a custom base query that uses the existing axios instance
const axiosBaseQuery =
  (): BaseQueryFn<
    {
      data?: unknown;
      headers?: Record<string, string>;
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
      params?: unknown;
      url: string;
      responseType?: 'json' | 'blob';
    },
    unknown,
    AxiosBaseQueryError,
    object,
    AxiosBaseQueryMeta
  > =>
  async (
    { data, headers, method = 'GET', params, url, responseType },
    { signal },
  ) => {
    const isCancelled = () => signal.aborted;
    const cancelled = () => ({
      error: { data: 'Request cancelled', status: 'CANCELLED' },
    });
    if (isCancelled()) return cancelled();
    try {
      const result = await mentoApi({
        data,
        headers,
        method,
        params,
        responseType,
        signal,
        url,
      });
      if (isCancelled()) return cancelled();
      return { data: result.data, meta: { status: result.status } };
    } catch (axiosError: unknown) {
      // Ignore late responses even when a transport does not honor cancellation.
      // In particular, never run old-session 401/403 redirect transforms.
      if (isCancelled()) return cancelled();
      const err = axiosError as {
        message?: string;
        response?: { data?: unknown; status?: number };
      };

      // Global MFA redirect fallback: if any endpoint returns a 403 with an MFA-required contract,
      // route the user to /mfa while preserving the intended redirect.
      try {
        const status = err.response?.status;
        const responseData = err.response?.data as
          | { error?: string; mfa_required?: boolean; mfaRequired?: boolean }
          | undefined;

        const mfaRequired =
          status === 403 &&
          (responseData?.error === 'mfa_required' ||
            responseData?.mfa_required === true ||
            responseData?.mfaRequired === true);

        if (mfaRequired && !window.location.pathname.includes('/mfa')) {
          const currentPath = window.location.pathname + window.location.search;
          const redirectUrl = `/mfa${
            currentPath ? `?redirect=${encodeURIComponent(currentPath)}` : ''
          }`;
          safeSetLocationHref(redirectUrl);
        }
      } catch {
        // Never break request handling on redirect logic.
      }

      return {
        error: {
          data: err.response?.data || err.message,
          status: err.response?.status,
        },
      };
    }
  };

const baseQuery = axiosBaseQuery();

export const api = createApi({
  baseQuery,
  endpoints: () => ({}),
  reducerPath: 'api',
  tagTypes: [
    'User',
    'Session',
    'Company',
    'Document',
    'GrowthPlan',
    'Assistant',
    'Note',
    'CoachProfile',
    'Milestone',
    'MilestoneStep',
    'Event',
    'MessageThread',
    'MessageThreadMessage',
    'Thread',
    'Function',
    'FunctionDisplay',
    'Survey',
    'SurveyTemplate',
    'Subscription',
    'UserEmployment',
    'Feedback',
    'DataImport',
    'SharedArtifact',
    'TypeformAdminSubmission',
    'TypeformAdminForm',
    'PulseReport',
    'MomentsApiSession',
    'MomentsArtifactFeedback',
    'MomentsArtifactSurvey',
    'MomentsArtifactNotes',
    'MomentsCoachMembers',
    'MomentsFeatureFlags',
    'CoachingNotes',
    'CoachingNotesSettings',
    'CoachingNotesHome',
    'McpConnectGrants',
    'AuthStatus',
    'Appointment',
    'CoachStatus',
    'CohortTag',
    'SchedulingAssistant',
  ],
});

// Export the api for use in store configuration
export default api;
